import { AppHelperService } from "@app/shared/services/app-helper.service";
import { ProjectListItemData } from "../model";

// When a creating / reading a project_view row in the DB, keys from here are used
// *** !!! So anything renamed here also needs to be renamed in the DB !!! ***
export type ProjectViewKey =
    | "accountNumber"
    | "address_area"
    | "building-type"
    | "city"
    | "coordinates"
    | "companyAccountId"
    | "country"
    | "currentSubDate"
    | "email"
    | "entityName"
    | "firstName"
    | "gate_access_code"
    | "home-builders"
    | "home-communities"
    | "iccid"
    | "imei"
    | "installDate"
    | "isOccupied"
    | "lastName"
    | "macAddress"
    | "monitoredStatus"
    | "name"
    | "onlineStatus"
    | "connectionType"
    | "phone"
    | "phone2"
    | "phone3"
    | "sCode"
    | "serialNumber"
    | "state"
    | "street"
    | "street2"
    | "timezonename"
    | "zipcode"
    | "uuid"
    | "zohoId"
    | "featureNestCameras"
    | "lockoutTime";

export interface ProjectViewColumn {
    // Fields that the API response must include
    dbKeys: Readonly<Array<keyof ProjectListItemData>>;
    // Column header to display
    viewHeader: string;
    // Generate a value-to-display
    getViewData: (d: ProjectListItemData) => any;
    // Each project_view row is a collection ProjectViewColumns identified by this key
    viewKey: ProjectViewKey;

    // Whether to display this column when editing / creating views
    permission?: string;
    // How to sort data in this column -- subset of ClareSort.dataSortType
    sortType?: "datetime";
}

/**
 * Selectable columns for account list
 *
 * The `as fromDbObject` casts are necessary because
 *   some of the destructured properties on ProjectListItemData are optional
 *   but we're assuming they're present when destructuring
 *   This is a safe assumption because of the dbKeys we define
 */
export const ALL_VIEW_COLUMNS: Record<ProjectViewKey, ProjectViewColumn> = {
    accountNumber: {
        dbKeys: ["accountNumber"],
        viewHeader: "Account Number",
        getViewData: ({ accountNumber }) => accountNumber,
        viewKey: "accountNumber",
    },
    address_area: {
        dbKeys: ["addressArea"],
        viewHeader: "Sq Ft / Sq M",
        getViewData: ({ addressArea }) => addressArea,
        viewKey: "address_area",
        permission: "viewSqtFtSqM",
    },
    "building-type": {
        dbKeys: ["buildingType"],
        viewHeader: "Building Type",
        getViewData: ({ buildingType }) => (buildingType ? JSON.parse(buildingType).name : ""),
        viewKey: "building-type",
        permission: "viewBuildingType",
    },
    city: {
        dbKeys: ["city"],
        viewHeader: "City",
        getViewData: ({ city }) => city,
        viewKey: "city",
    },
    companyAccountId: {
        dbKeys: ["companyAccountId"],
        viewHeader: "Company Account ID",
        getViewData: ({ companyAccountId }) => companyAccountId,
        viewKey: "companyAccountId",
        permission: "viewCompanyAccountID",
    },
    coordinates: {
        dbKeys: ["coordinates"],
        viewHeader: "Coordinates",
        getViewData: ({ coordinates }) => coordinates,
        viewKey: "coordinates",
        permission: "viewCoordinates",
    },
    country: {
        dbKeys: ["country"],
        viewHeader: "Country",
        getViewData: ({ country }) => country,
        viewKey: "country",
    },
    currentSubDate: {
        dbKeys: ["currentSubDate"],
        viewHeader: "Subscription Date",
        getViewData: ({ currentSubDate }) => (currentSubDate ? AppHelperService.getLocalDate(currentSubDate) : ""),
        viewKey: "currentSubDate",
        sortType: "datetime",
    },
    email: {
        dbKeys: ["email"],
        viewHeader: "Email",
        getViewData: ({ email }) => email,
        viewKey: "email",
    },
    entityName: {
        dbKeys: ["companyName"],
        viewHeader: "Company Name",
        getViewData: ({ companyName }) => companyName,
        viewKey: "entityName",
        permission: "viewCompanyName",
    },
    firstName: {
        dbKeys: ["firstName"],
        viewHeader: "First Name",
        getViewData: ({ firstName }) => firstName,
        viewKey: "firstName",
        permission: "viewUserName",
    },
    gate_access_code: {
        dbKeys: ["gateAccessCode"],
        viewHeader: "Gate Code",
        getViewData: ({ gateAccessCode }) => gateAccessCode,
        viewKey: "gate_access_code",
    },
    "home-builders": {
        dbKeys: ["builder"],
        viewHeader: "Builder",
        getViewData: ({ builder }) => (builder ? JSON.parse(builder).name : ""),
        viewKey: "home-builders",
        permission: "viewBuilder",
    },
    "home-communities": {
        dbKeys: ["community"],
        viewHeader: "Community",
        getViewData: ({ community }) => (community ? JSON.parse(community).name : ""),
        viewKey: "home-communities",
    },
    iccid: {
        dbKeys: ["iccid"],
        viewHeader: "ICCID",
        getViewData: ({ iccid }) => iccid,
        viewKey: "iccid",
    },
    imei: {
        dbKeys: ["imei"],
        viewHeader: "IMEI",
        getViewData: ({ imei }) => imei,
        viewKey: "imei",
    },
    installDate: {
        dbKeys: ["installDate"],
        viewHeader: "Install Date",
        getViewData: ({ installDate }) => (installDate ? AppHelperService.getLocalDate(installDate) : ""),
        viewKey: "installDate",
        sortType: "datetime",
    },
    isOccupied: {
        dbKeys: ["userinfo1Id"],
        viewHeader: "Occupancy",
        getViewData: ({ userinfo1Id }) => userinfo1Id !== 0,
        viewKey: "isOccupied",
    },
    lastName: {
        dbKeys: ["lastName"],
        viewHeader: "Last Name",
        getViewData: ({ lastName }) => lastName,
        viewKey: "lastName",
        permission: "viewUserName",
    },
    macAddress: {
        dbKeys: ["macAddress"],
        viewHeader: "Security MAC",
        getViewData: ({ macAddress }) => macAddress,
        viewKey: "macAddress",
        permission: "viewMACAddress",
    },
    // @TODO: Why are the firstName and lastName columns
    //        locked behind the viewUserName permission but this column isn't?
    name: {
        dbKeys: ["firstName", "lastName"],
        viewHeader: "Name",
        getViewData: ({ firstName, lastName }) => {
            if (!firstName || !lastName) {
                return "";
            }
            return `${lastName}, ${firstName}`;
        },
        viewKey: "name",
    },
    phone: {
        dbKeys: ["phone"],
        viewHeader: "Phone",
        getViewData: ({ phone }) => phone,
        viewKey: "phone",
    },
    phone2: {
        dbKeys: ["phone2"],
        viewHeader: "Phone 2",
        getViewData: ({ phone2 }) => phone2,
        viewKey: "phone2",
    },
    phone3: {
        dbKeys: ["phone3"],
        viewHeader: "Phone 3",
        getViewData: ({ phone3 }) => phone3,
        viewKey: "phone3",
    },
    monitoredStatus: {
        dbKeys: ["servicePlanId"],
        viewHeader: "Service Plan",
        // This is handled in selector
        getViewData: () => null,
        viewKey: "monitoredStatus",
    },
    sCode: {
        dbKeys: ["snaponeCode"],
        viewHeader: "SCode",
        getViewData: ({ snaponeCode }) => snaponeCode,
        viewKey: "sCode",
        permission: "viewSnapOne",
    },
    serialNumber: {
        dbKeys: ["serialNumber"],
        viewHeader: "Serial Number",
        getViewData: ({ serialNumber }) => serialNumber,
        viewKey: "serialNumber",
        permission: "viewSerialNumber",
    },
    state: {
        dbKeys: ["state"],
        viewHeader: "State",
        getViewData: ({ state }) => state,
        viewKey: "state",
    },
    street: {
        dbKeys: ["street"],
        viewHeader: "Address 1",
        getViewData: ({ street }) => street,
        viewKey: "street",
    },
    street2: {
        dbKeys: ["street2"],
        viewHeader: "Address 2",
        getViewData: ({ street2 }) => street2,
        viewKey: "street2",
    },
    timezonename: {
        dbKeys: ["timeZoneName"],
        viewHeader: "Time Zone",
        getViewData: ({ timeZoneName }) => timeZoneName,
        viewKey: "timezonename",
    },
    uuid: {
        dbKeys: ["uuid"],
        viewHeader: "UUID",
        getViewData: ({ uuid }) => uuid,
        viewKey: "uuid",
    },
    zipcode: {
        dbKeys: ["zipcode"],
        viewHeader: "Zip Code",
        getViewData: ({ zipcode }) => zipcode,
        viewKey: "zipcode",
    },
    zohoId: {
        dbKeys: ["zohoId"],
        viewHeader: "Zoho ID",
        getViewData: ({ zohoId }) => zohoId,
        viewKey: "zohoId",
        permission: "viewZoho",
    },
    // Special columns
    onlineStatus: {
        viewHeader: "Status",
        viewKey: "onlineStatus",
        // Selector will handle fetching data
        dbKeys: [],
        getViewData: () => null,
    },
    connectionType: {
        viewHeader: "Active Connection",
        viewKey: "connectionType",
        dbKeys: [],
        getViewData: () => null,
    },
    featureNestCameras: {
        dbKeys: [],
        viewHeader: "Feature - Nest",
        getViewData: ({ featureNestCameras }) => (featureNestCameras ? AppHelperService.getLocalDate(featureNestCameras) : ""),
        viewKey: "featureNestCameras",
        sortType: "datetime",
    },
    lockoutTime: {
        dbKeys: [],
        viewHeader: "Lockout Screen",
        getViewData: () => null, // date formatted in selector
        viewKey: "lockoutTime",
        sortType: "datetime",
    },
};
